import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, Building2, Scan, BarChart3, LayoutTemplate, Loader2, Plus, Trash2,
  ShieldCheck, LogOut, Activity, Pill, Send, Copy, RefreshCw, XCircle,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

async function safeCopy(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta);
    return true;
  } catch (e) { return false; }
}

function ResetPwButton({ userId, testid }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-link`, { origin: window.location.origin });
      await safeCopy(data.link);
      toast.success(data.email_sent ? "Reset link emailed + copied" : "Reset link copied (email failed)");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Button data-testid={testid} size="sm" variant="outline" disabled={!userId || busy} onClick={send}
      title={userId ? "Email a password reset link" : "No linked account — use Invite"} className="rounded-lg text-xs">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Reset PW"}
    </Button>
  );
}


export default function Admin() {
  const { logout, user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" /></div>
          <div><span className="text-lg font-bold text-slate-900 font-head">ReferU Admin</span><p className="text-xs text-slate-400 -mt-0.5">{user?.email}</p></div>
        </div>
        <Button data-testid="admin-logout" variant="ghost" onClick={logout} className="rounded-xl text-slate-600"><LogOut className="w-4 h-4 mr-1" /> Log out</Button>
      </header>

      <div className="max-w-6xl mx-auto p-6 md:p-8">
        <Tabs defaultValue="analytics">
          <TabsList className="rounded-xl flex-wrap h-auto">
            <TabsTrigger data-testid="tab-analytics" value="analytics" className="rounded-lg"><BarChart3 className="w-4 h-4 mr-1.5" /> Analytics</TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users" className="rounded-lg"><Users className="w-4 h-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger data-testid="tab-centres" value="centres" className="rounded-lg"><Building2 className="w-4 h-4 mr-1.5" /> Centres</TabsTrigger>
            <TabsTrigger data-testid="tab-pharmacies" value="pharmacies" className="rounded-lg"><Pill className="w-4 h-4 mr-1.5" /> Pharmacies</TabsTrigger>
            <TabsTrigger data-testid="tab-invitations" value="invitations" className="rounded-lg"><Send className="w-4 h-4 mr-1.5" /> Invitations</TabsTrigger>
            <TabsTrigger data-testid="tab-studies" value="studies" className="rounded-lg"><Scan className="w-4 h-4 mr-1.5" /> Studies</TabsTrigger>
            <TabsTrigger data-testid="tab-templates" value="templates" className="rounded-lg"><LayoutTemplate className="w-4 h-4 mr-1.5" /> Templates</TabsTrigger>
          </TabsList>
          <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="centres"><CentresTab /></TabsContent>
          <TabsContent value="pharmacies"><PharmaciesTab /></TabsContent>
          <TabsContent value="invitations"><InvitationsTab /></TabsContent>
          <TabsContent value="studies"><StudiesTab /></TabsContent>
          <TabsContent value="templates"><TemplatesTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Loading() { return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>; }

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const cards = [["Total Users", data.total_users], ["Doctors", data.doctors], ["Diagnostic Centres", data.centres], ["Pharmacies", data.pharmacies], ["Referrals", data.referrals], ["Prescriptions", data.prescriptions], ["Active Subs", data.active_subscriptions]];
  return (
    <div className="mt-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div data-testid="monthly-earnings-card" className="bg-blue-600 rounded-2xl p-6 shadow-sm text-white">
          <p className="text-sm text-blue-100">Monthly Earnings ({data.currency})</p>
          <p className="text-4xl font-bold font-head mt-2">{fmt(data.monthly_earnings)}</p>
          <p className="text-xs text-blue-100 mt-1">{data.paid_this_month || 0} paid subscription{(data.paid_this_month === 1) ? "" : "s"} this month</p>
        </div>
        <div data-testid="mrr-card" className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <p className="text-sm text-slate-500">Recurring Revenue (MRR)</p>
          <p className="text-4xl font-bold text-slate-900 font-head mt-2">{fmt(data.mrr)}</p>
          <p className="text-xs text-slate-400 mt-1">{data.active_subscriptions} active subscription{(data.active_subscriptions === 1) ? "" : "s"}</p>
        </div>
        <div data-testid="total-earnings-card" className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <p className="text-sm text-slate-500">Total Earnings (all time)</p>
          <p className="text-4xl font-bold text-slate-900 font-head mt-2">{fmt(data.total_earnings)}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <p className="text-sm text-slate-500">{l}</p><p className="text-3xl font-bold text-slate-900 font-head mt-2">{v}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 font-head mb-4">Most Requested Studies</h3>
          {data.top_studies?.length ? data.top_studies.map((s) => (
            <div key={s.name} className="flex items-center gap-3 mb-2.5">
              <span className="text-sm text-slate-600 w-40 truncate">{s.name}</span>
              <div className="flex-1 bg-slate-100 rounded-full h-2.5"><div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${(s.count / data.top_studies[0].count) * 100}%` }} /></div>
              <span className="text-sm font-semibold text-slate-700 w-6">{s.count}</span>
            </div>
          )) : <p className="text-slate-400 text-sm">No data yet.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 font-head mb-4">Referrals by Status</h3>
          {Object.entries(data.by_status || {}).map(([s, c]) => (
            <div key={s} className="flex justify-between py-1.5 border-b border-slate-50 text-sm"><span className="text-slate-600 capitalize">{s.replace("_", " ")}</span><span className="font-semibold">{c}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const action = async (id, act, days) => {
    try { await api.post(`/admin/users/${id}/action`, { action: act, days }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${edit.id}`, { full_name: edit.full_name, email: edit.email, phone: edit.phone, clinic_name: edit.clinic_name, clinic_address: edit.clinic_address, medical_reg_number: edit.medical_reg_number });
      toast.success("User updated"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.full_name} (${u.email})? This permanently removes the ${u.role === "radiology_centre" ? "centre and its account" : u.role === "pharmacy" ? "pharmacy and its account" : "account"}.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  if (!users) return <Loading />;
  return (
    <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="divide-y divide-slate-100">
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center justify-between p-4 gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">{u.full_name} <span className="text-xs font-normal text-slate-400 ml-1">{u.role.replace("_", " ")}</span></p>
              <p className="text-sm text-slate-500">{u.email}</p>
              {u.subscription && (
                <span className="text-xs">
                  <span className={u.subscription.can_create ? "text-emerald-600" : "text-amber-600"}>{u.subscription.status}</span>
                  {u.subscription.days_remaining != null && (
                    <span className="text-slate-400"> · {u.subscription.status === "trial" ? "trial ends" : "renews"} in {u.subscription.days_remaining} day{u.subscription.days_remaining === 1 ? "" : "s"}</span>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {u.role === "doctor" && (
                <>
                  <Button data-testid={`activate-${u.id}`} size="sm" variant="outline" onClick={() => action(u.id, "activate")} className="rounded-lg text-xs">Activate</Button>
                  <Button data-testid={`comp-${u.id}`} size="sm" variant="outline" onClick={() => action(u.id, "complimentary")} className="rounded-lg text-xs">Complimentary</Button>
                  <Button data-testid={`extend-${u.id}`} size="sm" variant="outline" onClick={() => action(u.id, "extend_trial", 14)} className="rounded-lg text-xs">+14d Trial</Button>
                  <Button data-testid={`suspend-${u.id}`} size="sm" variant="outline" onClick={() => action(u.id, "suspend")} className="rounded-lg text-xs text-amber-600">Suspend</Button>
                </>
              )}
              {u.role !== "admin" && (
                <>
                  <Button data-testid={`edit-user-${u.id}`} size="sm" variant="outline" onClick={() => setEdit({ ...u })} className="rounded-lg text-xs">Edit</Button>
                  <ResetPwButton userId={u.id} testid={`reset-user-${u.id}`} />
                  <Button data-testid={`delete-user-${u.id}`} size="sm" variant="outline" onClick={() => removeUser(u)} className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Edit {edit?.role?.replace("_", " ")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              {[["full_name", "Name"], ["email", "Email"], ["phone", "Phone"], ["clinic_name", edit.role === "pharmacy" ? "Pharmacy Name" : edit.role === "radiology_centre" ? "Centre Name" : "Clinic Name"], ["clinic_address", "Address"], ...(edit.role === "doctor" ? [["medical_reg_number", "Reg. Number"]] : [])].map(([k, l]) => (
                <div key={k}><Label>{l}</Label><Input data-testid={`edit-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              ))}
            </div>
          )}
          <DialogFooter><Button data-testid="save-user-edit" onClick={saveEdit} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CentresTab() {
  const [centres, setCentres] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", whatsapp: "" });
  const load = () => api.get("/centres").then((r) => setCentres(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/centres", form); toast.success("Centre added"); setOpen(false); setForm({ name: "", address: "", phone: "", email: "", whatsapp: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/centres/${edit.id}`, { name: edit.name, address: edit.address, phone: edit.phone, email: edit.email, whatsapp: edit.whatsapp, website: edit.website || "", hours: edit.hours || "", modalities: edit.modalities || [], branches: edit.branches || [], logo_file_id: edit.logo_file_id || null }); toast.success("Centre updated"); setEdit(null); load(); }
    catch (e) { toast.error("Failed to update"); }
  };
  const remove = async (id) => { try { await api.delete(`/centres/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };
  const invite = async (c) => {
    if (!c.email) return toast.error("Add an email to this centre first");
    try { const { data } = await api.post("/admin/invitations", { role: "radiology_centre", email: c.email, organization_name: c.name, origin: window.location.origin }); safeCopy(`${window.location.origin}/invite/${data.token}`); toast.success(data.email_sent ? "Invite emailed + link copied" : "Invite created — link copied"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!centres) return <Loading />;
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-centre-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> Add Centre</Button></DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-head">Add Radiology Centre</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {["name", "address", "phone", "email", "whatsapp"].map((k) => (
                <div key={k}><Label className="capitalize">{k}</Label><Input data-testid={`centre-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              ))}
            </div>
            <DialogFooter><Button data-testid="save-centre-admin" onClick={create} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
        {centres.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-4">
            <div><p className="font-semibold text-slate-900">{c.name}</p><p className="text-sm text-slate-500">{c.address} · {c.phone}</p></div>
            <div className="flex items-center gap-2">
              <Button data-testid={`edit-centre-${c.id}`} size="sm" variant="outline" onClick={() => setEdit({ ...c })} className="rounded-lg text-xs">Edit</Button>
              <ResetPwButton userId={c.linked_user_id} testid={`reset-centre-${c.id}`} />
              <Button data-testid={`invite-centre-${c.id}`} size="sm" variant="outline" onClick={() => invite(c)} className="rounded-lg text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Invite</Button>
              <button data-testid={`del-centre-${c.id}`} onClick={() => remove(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Edit Centre</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">{["name", "address", "phone", "email", "whatsapp", "website", "hours"].map((k) => (
            <div key={k}><Label className="capitalize">{k}</Label><Input data-testid={`edit-centre-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          ))}</div>}
          <DialogFooter><Button data-testid="save-centre-edit" onClick={saveEdit} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StudiesTab() {
  const [studies, setStudies] = useState(null);
  const [cat, setCat] = useState("X-Ray");
  const [name, setName] = useState("");
  const load = () => api.get("/studies").then((r) => setStudies(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    try { await api.post("/studies", { category: cat, name }); toast.success("Added"); setName(""); load(); } catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/studies/${id}`); load(); } catch (e) { toast.error("Failed"); } };
  if (!studies) return <Loading />;
  const cats = Object.keys(studies);
  return (
    <div className="mt-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex gap-3 flex-wrap items-end">
        <div className="w-44"><Label>Category</Label>
          <Select value={cat} onValueChange={setCat}><SelectTrigger data-testid="study-cat" className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="flex-1 min-w-[180px]"><Label>Study Name</Label><Input data-testid="study-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 rounded-xl" /></div>
        <Button data-testid="add-study-btn" onClick={add} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {Object.entries(studies).map(([c, items]) => (
          <div key={c} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 font-head mb-3">{c}</h3>
            <div className="space-y-1.5">
              {items.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm py-1">
                  <span className="text-slate-600">{s.name}</span>
                  <button data-testid={`del-study-${s.id}`} onClick={() => remove(s.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState(null);
  const [studies, setStudies] = useState({});
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); api.get("/studies").then((r) => setStudies(r.data)); }, []);
  const allStudies = Object.entries(studies).flatMap(([cat, items]) => items.map((i) => ({ category: cat, name: i.name })));
  const toggle = (s) => setSelected((p) => p.find((x) => x.name === s.name) ? p.filter((x) => x.name !== s.name) : [...p, s]);
  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    try { await api.post("/templates", { name, studies: selected, is_global: true }); toast.success("Global template created"); setOpen(false); setName(""); setSelected([]); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/templates/${id}`); load(); } catch (e) { toast.error("Failed"); } };
  if (!templates) return <Loading />;
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-global-template-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> New Global Template</Button></DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-head">Create Global Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="gtpl-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label>Studies</Label>
                <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {allStudies.map((s) => <button key={s.name} type="button" onClick={() => toggle(s)} className={`text-xs px-3 py-1.5 rounded-full border ${selected.find((x) => x.name === s.name) ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600"}`}>{s.name}</button>)}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-gtpl" onClick={create} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex justify-between"><h3 className="font-semibold text-slate-900 font-head">{t.name}</h3>
              <button data-testid={`del-gtpl-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>
            <p className="text-sm text-slate-500 mt-1">{t.studies?.map((s) => s.name).join(", ")}</p>
            {t.is_global && <span className="text-xs text-blue-500">Global</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


function PharmaciesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", license_number: "", pharmacist_in_charge: "", address: "", phone: "", email: "", whatsapp: "" });
  const load = () => api.get("/pharmacies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/pharmacies", form); toast.success("Pharmacy added"); setOpen(false); setForm({ name: "", license_number: "", pharmacist_in_charge: "", address: "", phone: "", email: "", whatsapp: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/pharmacies/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };
  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    try { await api.put(`/pharmacies/${edit.id}`, { name: edit.name, license_number: edit.license_number || "", pharmacist_in_charge: edit.pharmacist_in_charge || "", address: edit.address, phone: edit.phone, email: edit.email, whatsapp: edit.whatsapp, website: edit.website || "", hours: edit.hours || "", branches: edit.branches || [], logo_file_id: edit.logo_file_id || null }); toast.success("Pharmacy updated"); setEdit(null); load(); }
    catch (e) { toast.error("Failed to update"); }
  };
  const invite = async (c) => {
    if (!c.email) return toast.error("Add an email to this pharmacy first");
    try { const { data } = await api.post("/admin/invitations", { role: "pharmacy", email: c.email, organization_name: c.name, origin: window.location.origin }); safeCopy(`${window.location.origin}/invite/${data.token}`); toast.success(data.email_sent ? "Invite emailed + link copied" : "Invite created — link copied"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!items) return <Loading />;
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-pharmacy-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> Add Pharmacy</Button></DialogTrigger>
          <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">Add Pharmacy</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Pharmacy Name"], ["license_number", "License Number"], ["pharmacist_in_charge", "Pharmacist-in-charge"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["whatsapp", "WhatsApp"]].map(([k, l]) => (
                <div key={k}><Label>{l}</Label><Input data-testid={`pharm-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              ))}
            </div>
            <DialogFooter><Button data-testid="save-pharmacy-admin" onClick={create} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-4">
            <div><p className="font-semibold text-slate-900">{c.name}</p><p className="text-sm text-slate-500">{c.address} · {c.phone}</p></div>
            <div className="flex items-center gap-2">
              <Button data-testid={`edit-pharmacy-${c.id}`} size="sm" variant="outline" onClick={() => setEdit({ ...c })} className="rounded-lg text-xs">Edit</Button>
              <ResetPwButton userId={c.linked_user_id} testid={`reset-pharmacy-${c.id}`} />
              <Button data-testid={`invite-pharmacy-${c.id}`} size="sm" variant="outline" onClick={() => invite(c)} className="rounded-lg text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Invite</Button>
              <button data-testid={`del-pharmacy-${c.id}`} onClick={() => remove(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center text-slate-400">No pharmacies yet.</div>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Edit Pharmacy</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">{[["name", "Pharmacy Name"], ["license_number", "License Number"], ["pharmacist_in_charge", "Pharmacist-in-charge"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["whatsapp", "WhatsApp"], ["website", "Website"], ["hours", "Hours"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input data-testid={`edit-pharm-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
          ))}</div>}
          <DialogFooter><Button data-testid="save-pharmacy-edit" onClick={saveEdit} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const INV_STYLE = { sent: "bg-blue-50 text-blue-700", opened: "bg-indigo-50 text-indigo-700", activated: "bg-emerald-50 text-emerald-700", expired: "bg-amber-50 text-amber-700", revoked: "bg-red-50 text-red-600" };

function InvitationsTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "doctor", email: "", organization_name: "", phone: "" });
  const load = () => api.get("/admin/invitations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const inviteLink = (t) => `${window.location.origin}/invite/${t}`;
  const create = async () => {
    if (!form.email.trim()) return toast.error("Email required");
    try {
      const { data } = await api.post("/admin/invitations", { ...form, origin: window.location.origin });
      safeCopy(inviteLink(data.token));
      toast.success(data.email_sent ? "Invitation emailed + link copied" : "Invitation created — link copied (email not sent; verify sender domain)");
      setOpen(false); setForm({ role: "doctor", email: "", organization_name: "", phone: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copy = (t) => { safeCopy(inviteLink(t)); toast.success("Link copied"); };
  const waShare = (inv) => window.open(`https://wa.me/?text=${encodeURIComponent(`You're invited to ReferU. Activate your account: ${inviteLink(inv.token)}`)}`, "_blank");
  const resend = async (id) => { const { data } = await api.post(`/admin/invitations/${id}/resend`); safeCopy(inviteLink(data.token)); toast.success("Reissued — new link copied"); load(); };
  const revoke = async (id) => { await api.post(`/admin/invitations/${id}/revoke`); toast.success("Revoked"); load(); };
  if (!items) return <Loading />;
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-invite-btn" className="rounded-xl bg-blue-600 hover:bg-blue-700"><Send className="w-4 h-4 mr-1" /> Send Invitation</Button></DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-head">Send Account Invitation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role" className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="doctor">Doctor</SelectItem><SelectItem value="radiology_centre">Diagnostic Centre</SelectItem><SelectItem value="pharmacy">Pharmacy</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label>Name / Organization</Label><Input data-testid="invite-org" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label>Phone</Label><Input data-testid="invite-phone2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" /></div>
            </div>
            <DialogFooter><Button data-testid="save-invite" onClick={create} className="rounded-xl bg-blue-600 hover:bg-blue-700">Create & Copy Link</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
        {items.map((i) => (
          <div key={i.id} data-testid={`invite-${i.id}`} className="flex items-center justify-between p-4 gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">{i.email} <span className="text-xs font-normal text-slate-400 ml-1">{i.role.replace("_", " ")}</span></p>
              <p className="text-sm text-slate-500">{i.organization_name}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${INV_STYLE[i.status] || "bg-slate-100"}`}>{i.status}</span>
              {i.status !== "activated" && i.status !== "revoked" && (
                <>
                  <Button data-testid={`copy-invite-${i.id}`} size="sm" variant="outline" onClick={() => copy(i.token)} className="rounded-lg text-xs"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
                  <Button data-testid={`wa-invite-${i.id}`} size="sm" variant="outline" onClick={() => waShare(i)} className="rounded-lg text-xs">WhatsApp</Button>
                  <Button data-testid={`resend-invite-${i.id}`} size="sm" variant="outline" onClick={() => resend(i.id)} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Resend</Button>
                  <Button data-testid={`revoke-invite-${i.id}`} size="sm" variant="outline" onClick={() => revoke(i.id)} className="rounded-lg text-xs text-red-600"><XCircle className="w-3.5 h-3.5 mr-1" /> Revoke</Button>
                </>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center text-slate-400">No invitations yet.</div>}
      </div>
    </div>
  );
}

